/**
 * Home portfolio + property-analytics read contracts — RO-3-00f / #44473.
 *
 * Port of:
 *   - `excellent/app/_home/portfolio-rollup.tsx` (cause A)
 *   - `excellent/app/_home/property-analytics-card.tsx` (cause A)
 *
 * Both call `@excellent/core` measured-business seams (`portfolioSummary`,
 * `crossPropertyFunnel`, `propertyAnalytics`, `listProperties`,
 * `loadPropertyConfigs`) against the vault in their render path. The response
 * shapes below are exactly the rows those components render — not a general
 * property query surface.
 */

import { z } from "zod";
import type { Operation } from "../types";

const portfolioProperty = z.object({
  slug: z.string(),
  name: z.string(),
  revenueMinor: z.number(),
  payingParties: z.number(),
});
export type HomePortfolioProperty = z.infer<typeof portfolioProperty>;

const adoption = z.object({
  label: z.string(),
  value: z.number().nullable(),
  unit: z.string(),
});

const funnelIdentified = z.object({
  touchedFrom: z.number(),
  payingTo: z.number(),
  both: z.number(),
  exact: z.literal(true),
});

const funnelAnonymous = z.object({
  visitors: z.number(),
  signups: z.number(),
  signupRatePct: z.number(),
  exact: z.literal(false),
  note: z.string(),
});

const funnel = z.object({
  fromName: z.string(),
  toName: z.string(),
  fromSlug: z.string(),
  toSlug: z.string(),
  identified: funnelIdentified,
  anonymousTop: funnelAnonymous.nullable(),
  crossSellPct: z.number(),
});

const portfolioResponse = z.object({
  /** False when the roll-up should not render (no portfolio / nothing tracked). */
  visible: z.boolean(),
  propertyCount: z.number(),
  totalRevenueMinor: z.number(),
  payingCustomers: z.number(),
  payingCustomersLabel: z.string(),
  properties: z.array(portfolioProperty),
  adoptionBySlug: z.record(z.string(), adoption),
  funnel: funnel.nullable(),
});
export type HomePortfolioResponse = z.infer<typeof portfolioResponse>;

const portfolioQuery = z.object({
  workspaceId: z.coerce.number().int().optional(),
});

const portfolioOp: Operation<void, z.infer<typeof portfolioQuery>, void, HomePortfolioResponse> = {
  method: "GET",
  path: () => "/api/home/portfolio",
  query: portfolioQuery,
  response: portfolioResponse,
};

const analyticsMetric = z.object({
  metric: z.string(),
  label: z.string(),
  unit: z.string(),
  value: z.number().nullable(),
});

const propertyAnalyticsResponse = z.object({
  kind: z.string().nullable(),
  section: z.string().nullable(),
  metrics: z.array(analyticsMetric),
});
export type HomePropertyAnalyticsResponse = z.infer<typeof propertyAnalyticsResponse>;

const propertyAnalyticsQuery = z.object({
  workspaceId: z.coerce.number().int(),
  projectId: z.coerce.number().int(),
});

const propertyAnalyticsOp: Operation<
  void,
  z.infer<typeof propertyAnalyticsQuery>,
  void,
  HomePropertyAnalyticsResponse
> = {
  method: "GET",
  path: () => "/api/home/property-analytics",
  query: propertyAnalyticsQuery,
  response: propertyAnalyticsResponse,
};

export const homePortfolio = {
  portfolio: portfolioOp,
  propertyAnalytics: propertyAnalyticsOp,
};
